import React, { useEffect, useState } from 'react';
import { Loader2, Plus, Trash2, CheckCircle2 } from 'lucide-react';
import { formatDate } from '../client-portal/clientPortalApi';

const backendUrl = process.env.REACT_APP_BACKEND_URL;

const REVIEW_TYPES = [
  ['six_month_check_in', '6-month check-in'],
  ['annual_review', '12-month annual review'],
  ['fixed_rate_expiry', 'Fixed rate expiry'],
  ['interest_only_expiry', 'Interest-only expiry'],
  ['construction_review', 'Construction loan review'],
  ['preapproval_expiry', 'Pre-approval expiry'],
];

const STATUS_BADGES = {
  scheduled: 'bg-amber-50 text-amber-800 ring-amber-200',
  due_soon: 'bg-rose-50 text-rose-700 ring-rose-200',
  completed: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  snoozed: 'bg-slate-100 text-slate-700 ring-slate-200',
  cancelled: 'bg-slate-100 text-slate-500 ring-slate-200',
};

export default function AdminReviewsPanel({ clientId, token }) {
  const auth = { Authorization: `Bearer ${token}` };
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [newType, setNewType] = useState('annual_review');
  const [newDate, setNewDate] = useState('');
  const [newNotes, setNewNotes] = useState('');

  const reload = async () => {
    const r = await fetch(`${backendUrl}/api/admin/clients/${clientId}/reviews`, { headers: auth });
    if (r.ok) setItems(await r.json());
  };
  useEffect(() => { (async () => { try { await reload(); } finally { setLoading(false); } })(); /* eslint-disable-next-line */ }, [clientId]);

  const create = async () => {
    if (!newDate) return;
    await fetch(`${backendUrl}/api/admin/clients/${clientId}/reviews`, {
      method: 'POST',
      headers: { ...auth, 'Content-Type': 'application/json' },
      body: JSON.stringify({ review_type: newType, due_date: newDate, notes: newNotes }),
    });
    setShowNew(false);
    setNewDate('');
    setNewNotes('');
    reload();
  };

  const update = async (id, body) => {
    await fetch(`${backendUrl}/api/admin/reviews/${id}`, {
      method: 'PATCH',
      headers: { ...auth, 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    reload();
  };

  const remove = async (id) => {
    if (!window.confirm('Cancel this review?')) return;
    await fetch(`${backendUrl}/api/admin/reviews/${id}`, { method: 'DELETE', headers: auth });
    reload();
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;

  return (
    <div className="p-5 space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-wide text-slate-500 font-semibold">{items.length} review(s)</span>
        <button onClick={() => setShowNew(!showNew)} className="inline-flex items-center gap-1 text-sm text-emerald-700 hover:underline">
          <Plus className="w-3.5 h-3.5" /> Schedule review
        </button>
      </div>

      {showNew && (
        <div className="rounded-xl ring-1 ring-slate-200 bg-slate-50 p-3 space-y-2">
          <select value={newType} onChange={(e) => setNewType(e.target.value)} className="w-full px-2 py-1.5 rounded ring-1 ring-slate-200 bg-white text-sm">
            {REVIEW_TYPES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
          <input type="date" value={newDate} onChange={(e) => setNewDate(e.target.value)} className="w-full px-2 py-1.5 rounded ring-1 ring-slate-200 bg-white text-sm" />
          <input type="text" value={newNotes} onChange={(e) => setNewNotes(e.target.value)} placeholder="Notes (optional)" className="w-full px-2 py-1.5 rounded ring-1 ring-slate-200 bg-white text-sm" />
          <div className="flex justify-end gap-2">
            <button onClick={() => setShowNew(false)} className="text-sm text-slate-500 px-3 py-1">Cancel</button>
            <button onClick={create} className="px-3 py-1 rounded bg-emerald-500 hover:bg-emerald-600 text-white text-sm">Save</button>
          </div>
        </div>
      )}

      <ul className="space-y-2">
        {items.map((r) => (
          <li key={r.id} className="rounded-xl ring-1 ring-slate-200 bg-white p-3 text-sm">
            <div className="flex items-start gap-2 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-slate-900">{r.review_label}</div>
                <div className="text-xs text-slate-500">Due {formatDate(r.due_date)}{r.auto_created ? ' · auto-scheduled' : ''}</div>
                {r.notes && <div className="text-xs text-slate-600 mt-0.5">{r.notes}</div>}
              </div>
              <span className={`px-2 py-0.5 rounded-full ring-1 text-[11px] ${STATUS_BADGES[r.status] || ''}`}>{r.status.replace(/_/g, ' ')}</span>
              {r.status !== 'completed' && (
                <button onClick={() => update(r.id, { status: 'completed' })} className="p-1.5 rounded hover:bg-emerald-50 text-emerald-700" title="Mark complete">
                  <CheckCircle2 className="w-4 h-4" />
                </button>
              )}
              <button onClick={() => remove(r.id)} className="p-1.5 rounded hover:bg-rose-50 text-rose-600" title="Cancel">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
